import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const documentKeys = {
    all:        () => ['documents'],
    list:       (params) => [...documentKeys.all(), 'list', params],
    detail:     (id) => [...documentKeys.all(), 'detail', id],
    expiring:   (days) => [...documentKeys.all(), 'expiring', days],
    archived:   () => [...documentKeys.all(), 'archived'],
    accessLog:  (id) => [...documentKeys.all(), 'access-log', id],
    purgeQueue: () => [...documentKeys.all(), 'purge-queue'],
};

// GET /api/docs/
export function useDocuments(params = {}) {
    const { user } = useAuth();
    const orgId = localStorage.getItem('orgId') || '';
    const query = new URLSearchParams();
    if (params.category)        query.append('category', params.category);
    if (params.visibility)      query.append('visibility', params.visibility);
    if (params.search)          query.append('search', params.search);
    if (params.employee_id)     query.append('employee_id', params.employee_id);
    if (params.include_archived) query.append('include_archived', 'true');
    if (params.ordering)        query.append('ordering', params.ordering);
    const qs = query.toString();
    return useQuery({
        queryKey: documentKeys.list(params),
        queryFn:  () => apiHelper.getResource(`/docs/${qs ? `?${qs}` : ''}`, { orgId }),
        enabled:  !!user,
    });
}

// GET /api/docs/{id}/
export function useDocument(id) {
    const orgId = localStorage.getItem('orgId') || '';
    return useQuery({
        queryKey: documentKeys.detail(id),
        queryFn:  () => apiHelper.getResource(`/docs/${id}/`, { orgId }),
        enabled:  !!id,
    });
}

// POST /api/docs/ — multipart/form-data
export function useUploadDocument() {
    const queryClient = useQueryClient();
    const orgId = localStorage.getItem('orgId') || '';
    return useMutation({
        mutationFn: (formData) => apiHelper.postResource('/docs/', formData, { orgId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: documentKeys.all() });
        },
    });
}

// PATCH /api/docs/{id}/
export function useUpdateDocument() {
    const queryClient = useQueryClient();
    const orgId = localStorage.getItem('orgId') || '';
    return useMutation({
        mutationFn: ({ id, data }) => apiHelper.patchResource(`/docs/${id}/`, data, { orgId }),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: documentKeys.detail(id) });
            queryClient.invalidateQueries({ queryKey: documentKeys.list({}) });
        },
    });
}

// DELETE /api/docs/{id}/
export function useDeleteDocument() {
    const queryClient = useQueryClient();
    const orgId = localStorage.getItem('orgId') || '';
    return useMutation({
        mutationFn: (id) => apiHelper.deleteResource(`/docs/${id}/`, { orgId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: documentKeys.all() });
        },
    });
}

// GET /api/docs/{id}/download/
export function useDownloadDocument() {
    const orgId = localStorage.getItem('orgId') || '';
    return useMutation({
        mutationFn: ({ id, filename }) =>
            apiHelper.downloadResource(`/docs/${id}/download/`, filename, { orgId }),
    });
}

// GET /api/docs/expiring/?days=30
export function useExpiringDocuments(days = 30) {
    const { user } = useAuth();
    const orgId = localStorage.getItem('orgId') || '';
    return useQuery({
        queryKey: documentKeys.expiring(days),
        queryFn:  () => apiHelper.getResource(`/docs/expiring/?days=${days}`, { orgId }),
        enabled:  !!user,
    });
}

// GET /api/docs/archived/ — HR only
export function useArchivedDocuments() {
    const { user, hasAnyPermission } = useAuth();
    const orgId = localStorage.getItem('orgId') || '';
    const isHR = !!user?.is_superuser || hasAnyPermission(['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve']);
    return useQuery({
        queryKey: documentKeys.archived(),
        queryFn:  () => apiHelper.getResource('/docs/archived/', { orgId }),
        enabled:  !!user && isHR,
    });
}

// GET /api/docs/{id}/access-log/
export function useDocumentAccessLog(id) {
    const orgId = localStorage.getItem('orgId') || '';
    return useQuery({
        queryKey: documentKeys.accessLog(id),
        queryFn:  () => apiHelper.getResource(`/docs/${id}/access-log/`, { orgId }),
        enabled:  !!id,
    });
}

// GET /api/docs/purge-queue/ — HR only
export function usePurgeQueue() {
    const { user, hasAnyPermission } = useAuth();
    const orgId = localStorage.getItem('orgId') || '';
    const isHR = !!user?.is_superuser || hasAnyPermission(['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve']);
    return useQuery({
        queryKey: documentKeys.purgeQueue(),
        queryFn:  () => apiHelper.getResource('/docs/purge-queue/', { orgId }),
        enabled:  !!user && isHR,
    });
}
